
import React, { useState, useEffect } from 'react';
import { subscriptionService } from '../services/subscriptionService';
import { SubscriptionBox, UserSubscription, User } from '../types';
import { 
  Package, 
  Zap, 
  Calendar, 
  ShieldCheck, 
  X, 
  CheckCircle2, 
  Loader2, 
  ArrowRight,
  Plus,
  RefreshCw,
  Gift,
  Star,
  Info
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

interface Props {
  user: User | null;
  onNavigateHome: () => void;
}

const CustomerSubscriptions: React.FC<Props> = ({ user, onNavigateHome }) => {
  const [boxes, setBoxes] = useState<SubscriptionBox[]>([]);
  const [userSubs, setUserSubs] = useState<UserSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribing, setIsSubscribing] = useState<string | null>(null);
  const { format } = useCurrency();

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = () => {
    setIsLoading(true);
    setBoxes(subscriptionService.getAllBoxes());
    if (user) {
      setUserSubs(subscriptionService.getUserSubscriptions(user.id));
    }
    setIsLoading(false);
  };

  const handleSubscribe = async (box: SubscriptionBox) => {
    if (!user) return alert("Bhai, pehle sign in kar lo!");
    setIsSubscribing(box.id);
    await new Promise(resolve => setTimeout(resolve, 2000));
    subscriptionService.subscribeUser(user.id, box);
    loadData();
    setIsSubscribing(null);
  };

  const handleCancel = (subId: string) => {
    if (window.confirm("Bhai, kya aap sach mein cancel karna chahte hain? Sab exclusive deals miss ho jayengi!")) {
      subscriptionService.cancelSubscription(subId);
      loadData();
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="text-center md:text-left">
          <h1 className="text-5xl font-black text-gray-900 tracking-tight">Lynqar Subscriptions</h1>
          <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px] mt-2">Boutique Tech Delivered Recurringly</p>
        </div>
        <button onClick={onNavigateHome} className="bg-gray-100 text-gray-600 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-200 transition-all flex items-center gap-2">
           Explore One-Time Shop
        </button>
      </div>

      {userSubs.length > 0 && (
        <section className="space-y-8">
           <div className="flex items-center gap-3 border-b border-gray-100 pb-6">
              <ShieldCheck className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-black text-gray-900">My Active Plans</h2>
           </div>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {userSubs.filter(s => s.status === 'ACTIVE').map(sub => (
                <div key={sub.id} className="bg-gray-900 text-white p-10 rounded-[3rem] shadow-2xl relative overflow-hidden group">
                   <div className="absolute top-0 right-0 p-8 opacity-10"><Zap className="h-20 w-20" /></div>
                   <div className="relative space-y-6">
                      <div className="flex justify-between items-start">
                         <div>
                            <h3 className="text-2xl font-black">{sub.box?.name}</h3>
                            <p className="text-blue-400 text-[10px] font-black uppercase tracking-widest">{sub.box?.frequency} Subscription</p>
                         </div>
                         <span className="bg-emerald-500 text-white text-[8px] font-black px-3 py-1 rounded-full uppercase">Status: Live</span>
                      </div>
                      <div className="p-6 bg-white/5 rounded-3xl border border-white/10 flex items-center justify-between">
                         <div className="flex items-center gap-4">
                            <Calendar className="h-5 w-5 text-gray-400" />
                            <div>
                               <p className="text-[10px] text-gray-400 uppercase font-black">Next Delivery</p>
                               <p className="text-sm font-bold">{new Date(sub.nextBillingDate).toLocaleDateString()}</p>
                            </div>
                         </div>
                         <p className="text-2xl font-black text-blue-400">{format(sub.box?.price || 0)}</p>
                      </div>
                      <div className="flex gap-4">
                         <button onClick={() => handleCancel(sub.id)} className="flex-1 py-4 bg-white/10 text-white rounded-2xl font-black text-[10px] uppercase hover:bg-red-500/20 transition-all border border-white/10">Manage / Pause</button>
                      </div>
                   </div>
                </div>
              ))}
           </div>
        </section>
      )}

      <section className="space-y-12">
        <div className="text-center space-y-4 max-w-2xl mx-auto">
           <h2 className="text-4xl font-black text-gray-900 tracking-tight">Curated Tech Crates</h2>
           <p className="text-gray-500 font-medium leading-relaxed italic">"Bhai, ye boxes expert curators ne design kiye hain. Value guaranteed, surprise mandatory."</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
           {boxes.length === 0 ? (
             <div className="col-span-full py-20 text-center opacity-30">No active boxes available.</div>
           ) : (
             boxes.map(box => {
                const isSubscribed = userSubs.some(s => s.boxId === box.id && s.status === 'ACTIVE');
                return (
                  <div key={box.id} className="bg-white rounded-[40px] border border-gray-50 shadow-sm hover:shadow-2xl transition-all group flex flex-col overflow-hidden">
                     <div className="h-64 relative overflow-hidden bg-gray-100">
                        <img src={box.imageUrl} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt="" />
                        <div className="absolute inset-0 bg-black/10"></div>
                        <div className="absolute top-6 left-6">
                           <span className="bg-white text-gray-900 text-[9px] font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-xl flex items-center gap-2">
                              <Star className="h-3 w-3 text-blue-500 fill-current" /> Exclusive
                           </span>
                        </div>
                     </div>
                     <div className="p-10 space-y-6 flex-grow flex flex-col justify-between">
                        <div className="space-y-4">
                           <div className="flex justify-between items-start gap-4">
                              <h3 className="text-2xl font-black text-gray-900 leading-tight">{box.name}</h3>
                              <p className="text-xl font-black text-blue-600">{format(box.price)}</p>
                           </div>
                           <p className="text-sm text-gray-500 font-medium leading-relaxed">{box.description}</p>
                           <ul className="space-y-2 pt-4">
                              {box.items.slice(0, 3).map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-xs font-bold text-gray-600">
                                   <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div> {item}
                                </li>
                              ))}
                           </ul>
                        </div>

                        <button 
                          onClick={() => handleSubscribe(box)}
                          disabled={isSubscribed || isSubscribing === box.id}
                          className={`w-full mt-8 py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-xl ${
                             isSubscribed 
                             ? 'bg-emerald-50 text-emerald-600 cursor-default shadow-none border border-emerald-100' 
                             : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95 shadow-blue-100'
                          }`}
                        >
                           {isSubscribing === box.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 
                            isSubscribed ? <><CheckCircle2 className="h-4 w-4" /> Already Subscribed</> : 
                            <>Start Subscription <ArrowRight className="h-4 w-4" /></>}
                        </button>
                     </div>
                  </div>
                );
             })
           )}
        </div>
      </section>

      {/* Perks Section */}
      <section className="bg-gray-50 rounded-[4rem] p-12 md:p-20 grid grid-cols-1 md:grid-cols-3 gap-16">
         <div className="space-y-4">
            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-[2rem] flex items-center justify-center">
               <Gift className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-black text-gray-900 uppercase">Save up to 30%</h3>
            <p className="text-sm text-gray-500 leading-relaxed font-medium">Subscribers always pay less than the total retail value of individual items. Pure savings, bhai.</p>
         </div>
         <div className="space-y-4">
            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-[2rem] flex items-center justify-center">
               <RefreshCw className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-black text-gray-900 uppercase">Pause Anytime</h3>
            <p className="text-sm text-gray-500 leading-relaxed font-medium">Out of station? Not a problem. Skip a month or pause your sub with a single tap in your dashboard.</p>
         </div>
         <div className="space-y-4">
            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-[2rem] flex items-center justify-center">
               <Zap className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-black text-gray-900 uppercase">Early Access</h3>
            <p className="text-sm text-gray-500 leading-relaxed font-medium">New drops on Lynqar are available to subscribers 24 hours before the global marketplace launch.</p>
         </div>
      </section>
    </div>
  );
};

export default CustomerSubscriptions;
